/**
 * The parent's CURRENT location (the browser's Geolocation API), used as the class cards' distance
 * origin on search when they opt in with "Use my current location".
 *
 * "Ask once, then remember" (client decision): the opt-in is remembered in this browser
 * (localStorage, a per-viewer convenience), and the browser remembers the location permission
 * itself -- so on later visits the location is re-read silently, with no new prompt. If the
 * permission is later revoked, the opt-in is dropped and cards fall back to the saved home location.
 * Everything here is client-only and fails soft (no API, private mode, blocked storage).
 */

const PREF_KEY = 'kidhive.useCurrentLocation';

/** Whether the parent turned "Use my current location" on in this browser. */
export const readCurrentLocationPreference = () => {
  try {
    return typeof window !== 'undefined' && window.localStorage.getItem(PREF_KEY) === '1';
  } catch (e) {
    return false;
  }
};

export const writeCurrentLocationPreference = on => {
  try {
    if (on) {
      window.localStorage.setItem(PREF_KEY, '1');
    } else {
      window.localStorage.removeItem(PREF_KEY);
    }
  } catch (e) {
    // Storage blocked (private mode etc.): the toggle still works for this visit.
  }
};

export const isGeolocationAvailable = () =>
  typeof navigator !== 'undefined' && !!navigator.geolocation;

/** The location permission: 'granted' | 'denied' | 'prompt' | 'unknown' (API unsupported). */
export const geolocationPermission = async () => {
  try {
    if (typeof navigator === 'undefined' || !navigator.permissions?.query) {
      return 'unknown';
    }
    const status = await navigator.permissions.query({ name: 'geolocation' });
    return status?.state || 'unknown';
  } catch (e) {
    return 'unknown';
  }
};

/**
 * Read the device position once. Resolves { lat, lng }; rejects with { code: 'denied' |
 * 'unavailable' }. A position up to 10 minutes old is fine for "how far is this class".
 */
export const getDevicePosition = ({ timeout = 10000 } = {}) =>
  new Promise((resolve, reject) => {
    if (!isGeolocationAvailable()) {
      reject({ code: 'unavailable' });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      pos => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      err => reject({ code: err && err.code === 1 ? 'denied' : 'unavailable' }),
      { enableHighAccuracy: false, maximumAge: 10 * 60 * 1000, timeout }
    );
  });
